import { analytics } from '@analytics';
import { autorun } from 'mobx';
import { noop } from 'lodash';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';
import { OrderAtTableCarousel, CarouselScrollArrows } from '@components/carousel/order-at-table-carousel';
import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { HeadingWithFlameHighlights } from '@components/heading-with-flame-highlights';
import { theme } from '@constants/theme';
import { AddOfferModal } from '../../add-offer-modal';
import { Box, Flex, Container } from '../../grid';
import { OfferModal } from '../offer-modal';
import { P } from '../../typography/new';
import { useFeature } from '../../../hooks/use-feature';
import { BrazePromotionCard, OfferPromotionCard, PromotionCard } from '../promotion-card';
import { PromotionCardSkeleton } from '../promotion-card-skeleton';
import { LoadingIndicator } from '../../icons/loading-indicator';
import { DefaultState } from '../../default';

const LIST_TYPES = {
  SCROLL: 'default',
  CAROUSEL: 'carousel',
  VERTICAL: 'vertical',
};

const PROMOTION_CARD_SKELETON_ASPECT_RATIO = '300/184';

const buildTrackingPayloadPromo = ({ promo, location }) => ({
  name: promo?.header,
  description: promo?.description,
  location,
});

const buildTrackingPayloadOffer = ({ offer, location }) => ({
  name: offer?.name,
  description: offer?.description,
  location,
});

const PromotionList = ({
  customOpenModal,
  onlineOffersOnly,
  isCheckoutOfferVariant,
  analyticsLocation,
  type,
  ...rest
}) => {
  const [offerModalId, setOfferModalId] = useState(null);
  const [isAddOfferModalOpen, setIsAddOfferModalOpen] = useState(false);
  const { OfferStore, CartStore, MemberStore, BrazeStore, MenuStore } = useRootStore();
  const { isCateringMenu } = MenuStore;

  const loading = OfferStore.loading || MemberStore.loading;

  useEffect(() => {
    const disposer = autorun(() => {
      if (MemberStore.isSignedIn) {
        OfferStore.loadOffers();
        OfferStore.loadActivatedPromoCodes();
      }
    });
    return () => {
      disposer();
    };
  }, [MemberStore, OfferStore]);

  const { onlineOffers, allOffers } = OfferStore;
  const appliedOfferId = CartStore.offerId;

  const visibleOffers = onlineOffersOnly ? onlineOffers : allOffers;

  const showPromoCode = useFeature('promo-codes');

  const handleChange = offer => {
    const isApplied = offer.id === appliedOfferId;

    if (isApplied) {
      CartStore.removeOffer();
    } else {
      CartStore.updateOffer(offer);
      analytics.track(
        ANALYTICS_EVENTS.OFFER_APPLIED,
        buildTrackingPayloadOffer({ offer, location: analyticsLocation })
      );
    }
  };

  const handleClickPromoCard = promo => {
    analytics.track(ANALYTICS_EVENTS.PROMO_CLICKED, buildTrackingPayloadPromo({ promo, location: analyticsLocation }));
  };

  const handleOpenModal = offer => {
    analytics.track(ANALYTICS_EVENTS.OFFER_CLICKED, buildTrackingPayloadOffer({ offer, location: analyticsLocation }));
    analytics.track(ANALYTICS_EVENTS.OFFER_VIEWED, buildTrackingPayloadOffer({ offer, location: analyticsLocation }));

    if (customOpenModal) {
      return customOpenModal(offer);
    }
    return setOfferModalId(offer.id);
  };

  const addOfferModal = (
    <AddOfferModal
      {...{
        open: isAddOfferModalOpen,
        handleClose: () => {
          setIsAddOfferModalOpen();
        },
        openOfferModal: handleOpenModal,
        onOfferApply: offer =>
          analytics.track(
            ANALYTICS_EVENTS.OFFER_APPLIED,
            buildTrackingPayloadOffer({ offer, location: analyticsLocation })
          ),
      }}
    />
  );

  const addPromoCodeLink = showPromoCode && (
    <Box display="flex" height="100%" alignItems="center">
      <Box as="button" onClick={() => setIsAddOfferModalOpen(true)}>
        <P variant="text-link" textDecoration="underline" color={theme.colors.black} textTransform="none">
          Add promo code
        </P>
      </Box>
    </Box>
  );

  const buildOfferModalForOffer = offer =>
    offerModalId === offer.id &&
    !customOpenModal && (
      <OfferModal
        key={`modal-${offer.id}`}
        offer={offer}
        isApplied={offer.id === appliedOfferId}
        modalOpen={!!offerModalId}
        handleModalClose={() => {
          setOfferModalId(null);
          setIsAddOfferModalOpen(false);
        }}
        handleBack={isAddOfferModalOpen ? () => setOfferModalId(null) : null}
        onOfferChange={() => handleChange(offer)}
      />
    );

  // const launchDarklyPromotions = useFeature('order-at-table-promotions');
  // const brazePromotions = BrazeStore.getContentCardsByTypeAndFeatureFlags('order-at-table-promotions');
  let launchDarklyPromotions;
  let brazePromotions;

  const cateringPromotionsFeatureFlag = useFeature('catering-order-promotions');
  const standardPromotionsFeatureFlag = useFeature('order-at-table-promotions');

  if (isCateringMenu) {
    launchDarklyPromotions = cateringPromotionsFeatureFlag;
    brazePromotions = BrazeStore.getContentCardsByTypeAndFeatureFlags('catering-order-promotions');
  } else {
    launchDarklyPromotions = standardPromotionsFeatureFlag;
    brazePromotions = BrazeStore.getContentCardsByTypeAndFeatureFlags('order-at-table-promotions');
  }

  const carouselRef = useRef(null);

  const promos = useMemo(() => {
    if (isCheckoutOfferVariant) {
      return visibleOffers;
    }

    return [...visibleOffers, ...(brazePromotions || []), ...(launchDarklyPromotions || [])];
  }, [isCheckoutOfferVariant, visibleOffers, brazePromotions, launchDarklyPromotions]);

  if (isCateringMenu && promos?.length === 0) {
    return null;
  }

  // Vertical layout for account pages
  if (type === LIST_TYPES.VERTICAL) {
    return (
      <Flex flexDirection="column" width="100%" {...rest}>
        {addOfferModal}
        <P mb={{ xs: '16px', lg: '24px' }} variant="small" color="grey70">
          Only one offer can be applied per order*
        </P>
        {!loading && promos.length > 0 ? (
          <Flex flexDirection="column" gap={{ xs: '16px', lg: '24px' }}>
            {promos.map(promo => {
              const key = promo?.id || promo?.header;

              if (promo?.extras) {
                return (
                  <Box key={key}>
                    <BrazePromotionCard brazeData={promo.extras} />
                  </Box>
                );
              }

              if (promo?.id) {
                return (
                  <Box key={key}>
                    <OfferPromotionCard
                      offer={promo}
                      isApplied={promo.id === appliedOfferId}
                      onClick={() => handleOpenModal(promo)}
                    />
                    {buildOfferModalForOffer(promo)}
                  </Box>
                );
              }

              return (
                <Box key={key}>
                  <PromotionCard {...promo} onClick={() => handleClickPromoCard(promo)} />
                </Box>
              );
            })}
          </Flex>
        ) : (
          <Box my="24px">
            {loading ? (
              <LoadingIndicator />
            ) : (
              <DefaultState
                title="You currently don't have any active offers"
                subtitle="Offers you have available will appear here"
              />
            )}
          </Box>
        )}
      </Flex>
    );
  }

  // Default carousel layout
  return (
    <Box width="100%" {...rest}>
      {addOfferModal}
      <Container>
        <Flex
          flexDirection="row"
          alignItems="center"
          mb={isCheckoutOfferVariant ? promos?.length && '32px' : { xs: '24px', md: '32px' }}
          mx={0}
          justifyContent="space-between"
          gap="16px"
        >
          <Flex gap={isCheckoutOfferVariant ? '16px' : '32px'}>
            {isCheckoutOfferVariant ? (
              <P variant="large-semi-bold">Your Offers</P>
            ) : (
              <HeadingWithFlameHighlights marginBottom="0" heading="Promotions" isLabel variant="category" />
            )}
            <Box>{addPromoCodeLink}</Box>
          </Flex>

          {!!promos.length && !loading && (
            <CarouselScrollArrows carouselRef={carouselRef} position="center" showOnMobile={false} />
          )}
        </Flex>
      </Container>
      <OrderAtTableCarousel
        impressionEvent={ANALYTICS_EVENTS.WELCOME_PAGE_PROMOTIONS_SEEN}
        gap="16px"
        px={isCheckoutOfferVariant ? { xs: '16px', md: '32px' } : 0}
        fullWidth={!isCheckoutOfferVariant}
        mb="16px"
        ref={carouselRef}
      >
        {loading ? (
          <>
            <PromotionCardSkeleton role="menuitem" aspectRatio={PROMOTION_CARD_SKELETON_ASPECT_RATIO} />
            <PromotionCardSkeleton role="menuitem" aspectRatio={PROMOTION_CARD_SKELETON_ASPECT_RATIO} />
            <PromotionCardSkeleton role="menuitem" aspectRatio={PROMOTION_CARD_SKELETON_ASPECT_RATIO} />
            <PromotionCardSkeleton role="menuitem" aspectRatio={PROMOTION_CARD_SKELETON_ASPECT_RATIO} />
            <PromotionCardSkeleton role="menuitem" aspectRatio={PROMOTION_CARD_SKELETON_ASPECT_RATIO} />
          </>
        ) : (
          promos?.map(promo => {
            const key = promo?.id || promo?.header;

            if (promo?.extras) {
              return <BrazePromotionCard key={key} role="menuitem" brazeData={promo.extras} />;
            }

            if (promo?.id) {
              return (
                <Box key={key}>
                  <OfferPromotionCard
                    role="menuitem"
                    offer={promo}
                    isApplied={promo.id === appliedOfferId}
                    onClick={() => handleOpenModal(promo)}
                  />
                  {buildOfferModalForOffer(promo)}
                </Box>
              );
            }

            return <PromotionCard key={key} role="menuitem" {...promo} onClick={() => handleClickPromoCard(promo)} />;
          })
        )}
      </OrderAtTableCarousel>
    </Box>
  );
};

PromotionList.propTypes = {
  type: PropTypes.oneOf([LIST_TYPES.CAROUSEL, LIST_TYPES.SCROLL, LIST_TYPES.VERTICAL]),
  wasOfferUnlocked: PropTypes.bool,
  onOfferClick: PropTypes.func,
  onOfferApply: PropTypes.func,
  onOfferView: PropTypes.func,
  onOfferChange: PropTypes.func,
  customOpenModal: PropTypes.func,
  onlineOffersOnly: PropTypes.bool,
  isCheckoutOfferVariant: PropTypes.bool,
  analyticsLocation: PropTypes.string,
};

PromotionList.defaultProps = {
  type: LIST_TYPES.SCROLL,
  wasOfferUnlocked: null,
  onOfferClick: noop,
  onOfferApply: noop,
  onOfferView: noop,
  onOfferChange: noop,
  customOpenModal: null,
  onlineOffersOnly: false,
  isCheckoutOfferVariant: false,
  analyticsLocation: null,
};

const ObservedPromotionList = observer(PromotionList);
export { ObservedPromotionList as PromotionList };
